import { FunctionComponent, useEffect, useState } from 'react'
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, CircularProgress, Tooltip,
} from '@material-ui/core'
import { AlertTriangle, Check, X } from 'react-feather'
import { makeStyles } from '@material-ui/styles'
import { toast } from 'react-toastify'
import axios from '../../utils/axios'
import colors from '../../colors'
import { SPACING } from '../../constants'

interface OrganizationRow {
  id: number
  name: string
  slug: string
  tier: string
  serviceFee: string
  stripeConnected: boolean
  billingSetUp: boolean
  appleWalletEnabled: boolean
  liveMemberships: number
  members: number
  adminEmail: string | null
  createdAt: string
  issues: string[]
}

const ISSUE_COPY: Record<string, string> = {
  pro_without_billing: 'On Pro but nothing is being charged — no subscription on file',
  stripe_not_connected: 'No Stripe connection — cannot publish a membership or receive payouts',
}

const Flag: FunctionComponent<{ on: boolean }> = ({ on }) => (
  on
    ? <Check size={16} color={colors.GREEN} />
    : <X size={16} color={colors.GREY_4} />
)

const OrganizationsSection: FunctionComponent = () => {
  const styles = useStyles()
  const [rows, setRows] = useState<OrganizationRow[] | null>(null)

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get<OrganizationRow[]>('/super_admin/organization_overview')
        setRows(res.data)
      } catch {
        toast.error('Could not load organizations')
        setRows([])
      }
    })()
  }, [])

  if (!rows) {
    return <Box className={styles.section}><CircularProgress size={24} /></Box>
  }

  const needingAttention = rows.filter((r) => r.issues.length > 0).length

  return (
    <Box className={styles.section}>
      <h2 className={styles.heading}>Organizations</h2>
      <p className={styles.description}>
        {rows.length} total
        {needingAttention > 0 && ` · ${needingAttention} needing attention`}
      </p>

      <TableContainer className={styles.tableWrap}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Organization</TableCell>
              <TableCell>Admin</TableCell>
              <TableCell>Tier</TableCell>
              <TableCell align="right">Fee</TableCell>
              <TableCell align="center">Stripe</TableCell>
              <TableCell align="center">Billing</TableCell>
              <TableCell align="center">Wallet</TableCell>
              <TableCell align="right">Live</TableCell>
              <TableCell align="right">Members</TableCell>
              <TableCell>Joined</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((org) => (
              <TableRow key={org.id} hover>
                <TableCell>
                  <Box className={styles.nameCell}>
                    {org.issues.length > 0 && (
                      <Tooltip title={org.issues.map((i) => ISSUE_COPY[i] ?? i).join(' · ')}>
                        <span className={styles.warnIcon}><AlertTriangle size={15} /></span>
                      </Tooltip>
                    )}
                    <span>
                      <strong>{org.name}</strong>
                      <span className={styles.slug}>/{org.slug}</span>
                    </span>
                  </Box>
                </TableCell>
                <TableCell className={styles.email}>{org.adminEmail ?? '—'}</TableCell>
                <TableCell>
                  <span className={org.tier === 'pro' ? styles.tierPro : styles.tierCore}>
                    {org.tier.toUpperCase()}
                  </span>
                </TableCell>
                <TableCell align="right">{org.serviceFee}%</TableCell>
                <TableCell align="center"><Flag on={org.stripeConnected} /></TableCell>
                <TableCell align="center">
                  {/* Core orgs have nothing to bill, so absence is not a gap. */}
                  {org.tier === 'pro' ? <Flag on={org.billingSetUp} /> : <span className={styles.na}>—</span>}
                </TableCell>
                <TableCell align="center"><Flag on={org.appleWalletEnabled} /></TableCell>
                <TableCell align="right">{org.liveMemberships}</TableCell>
                <TableCell align="right">{org.members}</TableCell>
                <TableCell className={styles.date}>
                  {new Date(org.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

const useStyles = makeStyles({
  section: {
    border: `1px solid ${colors.GREY_5}`,
    borderRadius: 12,
    padding: SPACING * 3,
    background: colors.WHITE,
  },
  heading: { margin: 0, fontSize: 18, fontWeight: 700, color: colors.STITCH_BLACK },
  description: {
    marginTop: SPACING,
    marginBottom: SPACING * 2,
    fontSize: 13,
    color: colors.GREY_2,
  },
  tableWrap: { maxHeight: 520, overflowX: 'auto' },
  nameCell: { display: 'flex', alignItems: 'center', gap: 6 },
  warnIcon: { display: 'flex', color: colors.ORANGE },
  slug: { marginLeft: 6, fontSize: 12, color: colors.GREY_3 },
  email: { fontSize: 12, color: colors.GREY_1 },
  date: { fontSize: 12, color: colors.GREY_2, whiteSpace: 'nowrap' },
  na: { color: colors.GREY_4 },
  tierPro: {
    fontSize: 10, fontWeight: 700, letterSpacing: 0.4, padding: '2px 7px',
    borderRadius: 999, background: colors.PALE_BLUE, color: colors.BRIGHT_BLUE,
  },
  tierCore: {
    fontSize: 10, fontWeight: 700, letterSpacing: 0.4, padding: '2px 7px',
    borderRadius: 999, background: colors.GREY_6, color: colors.GREY_2,
  },
})

export default OrganizationsSection
